import React, { useEffect, useState, useRef } from "react";
import Header from "../Components/Header/Header";
import Footer from "../Components/Footer/Footer";
import axiosInstance from "../Axios/AxiosInstance";
import ReusableTable from "../Components/Table/ReusableTable";
import { MdDownload, MdFilterAltOff } from "react-icons/md";
import DatePicker from "../Components/DatePicker/DatePicker";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BsCalendar3 } from "react-icons/bs";

const ProcessOrder = () => {
  const [orderData, setOrderData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDates, setSelectedDates] = useState({ startDate: '', endDate: '' });

  const pageDataRef = useRef({});
  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowDatePicker(false)
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatDate = (date) => {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
  };

  const formatDateDisplay = (date) => {
    const [year, month, day] = date.split('-');
    return `${day}-${month}-${year}`;
  };

  const getCurrentDate = () => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (searchInput === '') {
      pageDataRef.current = {};
      fetchOrderData();
    }
  }, [searchInput]);

  useEffect(() => {
    fetchOrderData();
  }, [currentPage, rowsPerPage, searchInput, selectedDates]);


  const fetchOrderData = () => {
    const cacheKey = `${currentPage}-${rowsPerPage}-${selectedDates.startDate || ""}-${selectedDates.endDate || ""}-${searchInput}`;

    if (pageDataRef.current[cacheKey]) {
      setOrderData(pageDataRef.current[cacheKey]);
      setIsLoading(false);
    } else {
      setIsLoading(true);
      axiosInstance
        .post("/admindashboard/ProcessOrderList", {
          fromDate: selectedDates.startDate,
          toDate: selectedDates.endDate,
          dealerId: 0,
          painterId: 0,
          pageNumber: currentPage,
          pageSize: rowsPerPage,
          searchItem: searchInput,
        })
        .then((response) => {
          const { processOrderList, paginatedData } = response.data;

          const updatedProcessOrderList = processOrderList.map((order) => {
            const [day, month, year] = order.createdDate.substring(0, 10).split("-");
            return {
              ...order,
              createdDate: `${day}/${month}/${year}`,
            };
          });

          setOrderData(updatedProcessOrderList);
          setTotalRecords(paginatedData.totalRecords);
          // pageDataRef.current[cacheKey] = updatedProcessOrderList;
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          console.error("Error fetching order data:", error);
        });
    }
  };


  const clearFilters = () => {
    setSelectedDates({ startDate: '', endDate: '' });
    setSearchInput('');
  };

  const columns = [
    { key: "createdDate", label: "Date" },
    { key: "orderNumber", label: "Process Order Number" },
    { key: "batchNumber", label: "Batch Number" },
    { key: "tokenGeneratedCount", label: "Token Generated Count" },
    { key: "tokenScannedCount", label: "Token Scanned Count" },
    { key: "factoryScanCount", label: "Quality Check" },
    { key: "material", label: "Material Code" },
    { key: "matrialDesc", label: "Material Description" },
  ];

  const handleSelectedRowsChange = (newSelectedRows) => {
    setSelectedRows(newSelectedRows);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handleDateSubmit = (dates) => {
    const formattedDates = {
      startDate: formatDate(dates.startDate),
      endDate: formatDate(dates.endDate),
    };
    setSelectedDates(formattedDates);
    setShowDatePicker(false);
  };

  const handleDownload = () => {
    axiosInstance
      .post('admindashboard/ProcessOrderListDownload', {
        fromDate: selectedDates.startDate,
        toDate: selectedDates.endDate,
        dealerId: 0,
        painterId: 0,
        searchItem: "",
        pageNumber: 0,
        pageSize: 0,
      }, {
        responseType: 'json',
      })
      .then(response => {
        const data = response.data;

        if (!data || data.length === 0) {
          toast.error('There is no data in the selected date range');
          return;
        }

        if (Array.isArray(data) && data.length > 0) {
          const csvData = convertToCSV(data);
          const currentDate = getCurrentDate();
          const filename = `ProcessOrderReport_${currentDate}.csv`;
          downloadCSV(csvData, filename);
        } else {
          console.error('No data available to export.');
        }
      })
      .catch(error => {
        console.error('Error downloading data:', error);
      });
  };

  const convertToCSV = (data) => {
    const columnMapping = {
      createdDate: "Created Date",
      orderNumber: "Process Order Number",
      batchNumber: "Batch Number",
      tokenGeneratedCount: "Token Generated Count",
      tokenScannedCount: "Token Scanned Count",
      factoryScanCount: "Factory Scanned Count",
      material: "Material Code",
      matrialDesc: "Material Description",
    };

    const headers = Object.values(columnMapping);
    const keys = Object.keys(columnMapping);

    const csvRows = data.map(row =>
      keys.map(key => JSON.stringify(row[key] || "", null, 2)).join(",")
    );

    return [headers.join(","), ...csvRows].join("\n");
  };

  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
  };

  const handleSearchInputChange = (e) => {
    const newSearchValue = e.target.value;
    setSearchInput(newSearchValue);
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header title={"Process Order"} />
      <div className="flex flex-col flex-1 mx-2 my-2">

        <div className="flex flex-wrap justify-between items-center mt-2 mb-1">
          <div className="flex items-center gap-4 ml-1">
            <input
              type="text"
              placeholder="Search by Process order/Material code"
              className="px-4 py-2 border border-gray-300 rounded-lg w-[300px] focus:outline-none focus:ring-2 focus:ring-black-500"
              value={searchInput}
              onChange={handleSearchInputChange}
            />
            {selectedDates.startDate && (
              <div className="ml-2 flex space-x-3">
                <span className="bg-[#1e365e] text-white px-3 py-2 rounded-lg shadow-md">
                  From: {formatDateDisplay(selectedDates.startDate)}
                </span>
                <span className="bg-[#1e365e] text-white px-4 py-2 rounded-lg shadow-md ">
                  To: {formatDateDisplay(selectedDates.endDate)}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 mr-2">
            <span
              className="text-[#1e365e] cursor-pointer hover:text-[#1e365e]  "
              onClick={clearFilters}
              title="Clear Filter"
            >
              <MdFilterAltOff size={32} />
            </span>
            <span
              className="text-[#1e365e] cursor-pointer hover:text-[#1e365e]  "
              onClick={() => setShowDatePicker(!showDatePicker)}
              title="Date Filter"
            >
              <BsCalendar3 size={25} />
            </span>
            <span
              className="text-[#1e365e] cursor-pointer hover:text-[#1e365e]  "
              onClick={handleDownload}
              title="Download"
            >
              <MdDownload size={31} />
            </span>
          </div>
        </div>
        <div className="mt-2 p-1">
          <ReusableTable
            data={orderData}
            columns={columns}
            rowsPerPage={rowsPerPage}
            loading={isLoading}
            currentPage={currentPage}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSelectedRowsChange={handleSelectedRowsChange}
          />
        </div>
        {showDatePicker && (
          <dialog open
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              border: "none",
              background: "none",
              maxHeight: "70vh",
              overflowY: "auto",
            }}
            ref={popupRef}>
            <div >
              <DatePicker onCancel={() => setShowDatePicker(false)} onSubmit={handleDateSubmit} />
            </div>
          </dialog>
        )}
      </div>
      <Footer />
      <ToastContainer />
    </div>
  );
};

export default ProcessOrder;
